import React from 'react';
import { Calendar, Zap, Video, Brain, Headphones, Activity, Lock, ChevronRight } from 'lucide-react';
import { useFeatureGate } from '../contexts/FeatureGateContext';

const UpcomingFeaturesCard: React.FC = () => {
  const { getUpcomingFeatures, getWeekNumber, accelerateUnlocks } = useFeatureGate();
  
  const upcomingFeatures = getUpcomingFeatures();
  const currentWeek = getWeekNumber();
  
  if (upcomingFeatures.length === 0 || currentWeek >= 4) {
    return null;
  }

  const getFeatureIcon = (featureId: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'avatar_companion': <Video className="w-4 h-4 text-purple-500" />,
      'audio_library': <Headphones className="w-4 h-4 text-green-500" />,
      'advanced_analytics': <Brain className="w-4 h-4 text-blue-500" />,
      'detailed_behavioral': <Activity className="w-4 h-4 text-orange-500" />,
      'custom_audio': <Headphones className="w-4 h-4 text-green-500" />,
      'therapist_matching': <Users className="w-4 h-4 text-purple-500" />,
      'behavioral_science': <Brain className="w-4 h-4 text-blue-500" />,
      'research_participation': <Zap className="w-4 h-4 text-yellow-500" />,
      'advanced_privacy': <Shield className="w-4 h-4 text-slate-500" />
    };
    
    return iconMap[featureId] || <Lock className="w-4 h-4 text-slate-500" />;
  };

  const getFeatureName = (featureId: string) => {
    const nameMap: Record<string, string> = {
      'avatar_companion': 'AI Avatar Companion',
      'audio_library': 'Audio Library',
      'goal_adjustment': 'Goal Adjustment',
      'basic_behavioral': 'Behavioral Patterns',
      'advanced_analytics': 'Advanced Analytics',
      'detailed_behavioral': 'Detailed Behavioral Insights',
      'custom_audio': 'Custom Audio Creation',
      'therapist_matching': 'Therapist Matching',
      'behavioral_science': 'Behavioral Science Dashboard',
      'research_participation': 'Research Participation',
      'advanced_privacy': 'Advanced Privacy Controls',
      'community_features': 'Community Features'
    };
    
    return nameMap[featureId] || featureId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-teal-600" />
          <h3 className="font-semibold text-slate-900">Coming Soon</h3>
        </div>
        <span className="text-xs text-slate-500">Week {currentWeek}</span>
      </div>
      
      <div className="space-y-2 mb-3">
        {upcomingFeatures.slice(0, 3).map(({ feature, daysUntilUnlock }) => (
          <div key={feature} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              {getFeatureIcon(feature)}
              <span className="text-sm text-slate-700">{getFeatureName(feature)}</span>
            </div>
            <span className="text-xs text-slate-500">
              {daysUntilUnlock === 0 ? 'Today' : 
               daysUntilUnlock === 1 ? 'Tomorrow' : 
               `${daysUntilUnlock} days`}
            </span>
          </div>
        ))}
        
        {upcomingFeatures.length > 3 && (
          <div className="text-xs text-center text-slate-500">
            +{upcomingFeatures.length - 3} more coming soon
          </div>
        )}
      </div>
      
      <button
        onClick={accelerateUnlocks}
        className="w-full flex items-center justify-center gap-1 py-2 px-3 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-sm"
      >
        <Zap className="w-4 h-4" />
        Accelerate Unlocks
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

const Users = (props: any) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    {...props}
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const Shield = (props: any) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

export default UpcomingFeaturesCard;